import { useEffect, useState } from "react";
import { apiFetch } from "../lib/api.js";
import PremiumLock, { usePaid } from "../components/PremiumLock.jsx";

const DEFAULT_COOLDOWN_MS = 60 * 60 * 1000;

function formatRemaining(ms) {
  const minutes = Math.ceil(ms / 60000);
  return minutes <= 1 ? "1 minute" : `${minutes} minutes`;
}

export default function Insight() {
  const isPaid = usePaid();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [reportError, setReportError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isPaid) return;
    apiFetch("/insight/trends").then(setData).catch((e) => setError(e.message));
    apiFetch("/insight/report").then(setReport).catch((e) => setReportError(e.message));
  }, [isPaid]);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  const cooldownMs = report?.cooldownMs ?? DEFAULT_COOLDOWN_MS;
  const remainingCooldownMs = report?.latest
    ? Math.max(0, cooldownMs - (now - new Date(report.latest.createdAt).getTime()))
    : 0;
  const onCooldown = remainingCooldownMs > 0;

  async function refreshInsight() {
    setRefreshing(true);
    setReportError(null);
    try {
      const updated = await apiFetch("/insight/report", { method: "POST" });
      setReport(updated);
      setExpandedHistoryId(null);
      setNow(Date.now());
    } catch (e) {
      setReportError(e.message);
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <div className="page-wide">
      <div className="kicker" style={{ marginBottom: 12 }}>
        <span className="the-line" />
        INSIGHT
      </div>
      <h1 style={{ marginBottom: 32 }}>How you're moving</h1>

      {!isPaid && (
        <PremiumLock title="Trends across time">
          See how your Standing Check scores and pattern register have changed across every
          attempt and session with your corner. Available on Conquest Premium - coming soon.
        </PremiumLock>
      )}

      {error && <p style={{ color: "var(--error)" }}>{error}</p>}

      {isPaid && report && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <div className="card-title">AI insight report</div>
            <button
              className="btn-secondary"
              onClick={refreshInsight}
              disabled={refreshing || onCooldown}
              title={onCooldown ? `Available again in ${formatRemaining(remainingCooldownMs)}` : undefined}
            >
              {refreshing
                ? "Generating…"
                : onCooldown
                ? `Available again in ${formatRemaining(remainingCooldownMs)}`
                : "Refresh insight"}
            </button>
          </div>

          {reportError && <p style={{ color: "var(--error)", marginBottom: 12 }}>{reportError}</p>}

          {!report.eligible && !report.latest && (
            <p className="empty-state">
              Not enough history yet - take your Standing Check or finish a session with your corner first, then
              come back to generate your first insight report.
            </p>
          )}

          {report.latest && (
            <>
              <div style={{ color: "var(--muted)", fontSize: 13, marginBottom: 12 }}>
                Generated {new Date(report.latest.createdAt).toLocaleDateString()}
              </div>
              <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6, marginBottom: report.history.length > 1 ? 20 : 0 }}>
                {report.latest.reportText}
              </div>
            </>
          )}

          {!report.latest && report.eligible && (
            <p className="empty-state">
              You have enough history now - click "Refresh insight" above to generate your first
              report.
            </p>
          )}

          {report.history.length > 1 && (
            <div style={{ borderTop: "1px solid var(--hairline)", paddingTop: 16 }}>
              <div style={{ color: "var(--muted)", fontSize: 13, marginBottom: 8 }}>Previous reports</div>
              {report.history.slice(1).map((h) => (
                <div key={h.id} style={{ marginBottom: 8 }}>
                  <button
                    className="btn-secondary"
                    style={{ fontSize: 12 }}
                    onClick={() => setExpandedHistoryId(expandedHistoryId === h.id ? null : h.id)}
                  >
                    {new Date(h.createdAt).toLocaleDateString()}
                  </button>
                  {expandedHistoryId === h.id && (
                    <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6, marginTop: 8, fontSize: 14 }}>
                      {h.reportText}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {isPaid && data && (
        <>
          <div className="card" style={{ marginBottom: 24 }}>
            <div className="card-title" style={{ marginBottom: 16 }}>Standing Check over time</div>
            {data.diagnosticHistory.length === 0 && (
              <p className="empty-state">No Standing Check history yet. Take one and the trend starts here.</p>
            )}
            {data.diagnosticHistory.length > 0 && (
              <div className="hairline-list">
                {data.diagnosticHistory.map((d, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>{new Date(d.takenAt).toLocaleDateString()}</span>
                    <span className={`band-label band-${d.overallBand}`}>{d.overallBand}</span>
                    <span className="mono">{d.overallPercent}%</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-title" style={{ marginBottom: 16 }}>Pattern register evolution</div>
            {data.patternRegisterHistory.length === 0 && (
              <p className="empty-state">No sessions have closed out yet - the register will build up as you run them.</p>
            )}
            {data.patternRegisterHistory.map((snap, i) => (
              <div key={i} style={{ marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid var(--hairline)" }}>
                <div style={{ color: "var(--muted)", fontSize: 13, marginBottom: 8 }}>
                  {new Date(snap.takenAt).toLocaleDateString()}
                </div>
                {snap.patternsRegister.length === 0 ? (
                  <p className="empty-state" style={{ marginBottom: 0 }}>No patterns recorded yet at this point.</p>
                ) : (
                  snap.patternsRegister.map((p, j) => (
                    <div key={j} style={{ marginBottom: 6 }}>
                      {p.number}. {p.pattern}
                      {p.fix && <span style={{ color: "var(--muted)" }}> - fix: {p.fix}</span>}
                    </div>
                  ))
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
